package com.neurobs.react

import org.scalajs.dom.{HTMLDivElement, HTMLElement, HTMLTableRowElement}
import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.useEffect
import slinky.core.facade.React
import slinky.web.html._

import scala.scalajs.js

case class ListViewProperties
(
	header: Seq[String],
	rows: Seq[Seq[String]],
	selectIndex: Option[Int]
)

trait ListViewAdapter[T, V] {
	def header: Seq[String]
	def makeRow(t: T): Seq[String]
	def makeValue(t: T): V
}

/**
 * Helper class that populates a list-view control given a sequence of objects of type T
 *
 * @param adapter Generates content for each object for the control
 * @param items The items in the control
 * @param desiredSelectIndex The selected item, which is validated first
 * @tparam T
 * @tparam V Type of the "value" method.
 */
class ListViewState[T, V] (adapter: ListViewAdapter[T, V], val items: Seq[T], desiredSelectIndex: Option[Int]) {

	val selectIndex: Option[Int] = desiredSelectIndex.flatMap(v => if (v < items.size) Some(v) else None)

	def generateProperties(): ListViewProperties = {
		ListViewProperties(adapter.header, items.map(adapter.makeRow(_)), selectIndex)
	}

	def value: Option[V] = selectIndex.map(i => adapter.makeValue(items(i)))

	def indexOf(t: T): Option[Int] = {
		val i = items.indexOf(t)
		if (i == -1) None else Some(i)
	}

	def newSelection(newSelectIndex: Option[Int]): ListViewState[T, V] = new ListViewState[T, V](adapter, items, newSelectIndex)

	def newSelection(t: T): ListViewState[T, V] = new ListViewState[T, V](adapter, items, indexOf(t))

	def newItems(i: Seq[T], newDesiredSelectIndex: Option[Int] = None): ListViewState[T, V] = new ListViewState[T, V](adapter, i, newDesiredSelectIndex)
}

/**
 * Handles a single-selectable list-view control with multiple columns.
 */
@react object ListView {
	/**
	 * @param props The contents of the control
	 * @param onSelectionChange Called with any new selection
	 * @param emMaxHeight Maximum height (with vertical scrollbar) or none to be sized to content
	 * @param scrollToSelectionCounter If this value has changed since the last render, the control will
	 *                                 scroll itself so the selected item (if any) is visible.
	 */
	case class Props
	(
		props: ListViewProperties,
		onSelectionChange: Option[Int] => Unit,
		emMaxHeight: Option[Double] = None,
		scrollToSelectionCounter: Int = 0
	)

	val component = FunctionalComponent[Props]{ props =>
		val styleDict = js.Dynamic.literal()
		props.emMaxHeight.foreach { h =>
			styleDict.maxHeight = s"${h}em"
			styleDict.overflowY = "auto"
		}

		val scrollBoxRef = React.createRef[HTMLDivElement]
		val selectRef = React.createRef[HTMLTableRowElement]
		val bodyRef = React.createRef[HTMLElement]
		useEffect(() => {
			if (selectRef.current != null && scrollBoxRef.current != null && bodyRef.current != null) {
				scrollBoxRef.current.scrollTop = selectRef.current.offsetTop - bodyRef.current.offsetTop
			}
		}, Vector(props.scrollToSelectionCounter))

		div(ref := scrollBoxRef, style := styleDict)(
			table(className := "ui compact selectable celled table")(
				thead(style := StyleUtil.makeStyle(("position", "sticky"), ("top", "0px")))(
					tr(
						props.props.header.map { h =>
							th(key := h)(h)
						}
					)
				),
				tbody(ref := bodyRef, style := styleDict)(
					props.props.rows.zipWithIndex.map { r =>
						val isSelection = Some(r._2) == props.props.selectIndex
						tr(
							if (isSelection) (ref := selectRef) else null,
							className := (if (isSelection) "active" else ""),
							onClick := {() => props.onSelectionChange(Some(r._2)) },
							key := r._2.toString
						)(
							r._1.zipWithIndex.map(e => td(key := e._2.toString)(e._1))
						)
					}
				)
			)
		)
	}
}
